import {
  presentB2CPaidModalAtom,
  presentDesjardinsModalAtom,
  presentNotificationModalAtom,
  showIntroAfterDesjardinsModalAtom,
} from 'app/atoms/ui';
import { Column } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Modal } from 'app/design/primitives/modal';
import { Text } from 'app/design/primitives/text';
import { Event, track } from 'app/lib/analytics';
import { useCreateOnePlan } from 'app/network/plan';
import { useOnePreference } from 'app/network/preferences';
import { useMyProfile } from 'app/network/profile';
import { createTheme as createDSTheme } from 'app/shopify';
import { TrialModalType } from 'app/types/preferences';
import { IconRosetteDiscountCheck } from '@tabler/icons-react-native';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const theme = createDSTheme();

/**
 * Self-contained Desjardins membership prompt modal.
 *
 * This modal is triggered from `useAppStartModals` (which runs in `landing-page`).
 * It must be rendered on `landing-page` so that it is mounted and visible when
 * `presentDesjardinsModalAtom` is set to `true` by the modal orchestration logic.
 * Rendering it here (rather than on `all-listings`) ensures that
 * `presentDesjardinsModalAtom` is properly cleared after the user responds,
 * which unblocks the `intro-after-signup` modal from firing.
 */
export function DesjardinsModalPrompt() {
  const { t } = useTranslation('all-listings');

  const notificationsModalVisible = useAtomValue(presentNotificationModalAtom);
  const [isDesjardinsModalOpen, setIsDesjardinsModalOpen] = useAtom(presentDesjardinsModalAtom);
  const [isActivatingDesjardins, setIsActivatingDesjardins] = useState(false);
  const setShowIntroAfterDesjardinsModal = useSetAtom(showIntroAfterDesjardinsModalAtom);
  const userTrialModalPreference = useOnePreference('trialModal').data?.value;
  const presentB2cPaidModal = useSetAtom(presentB2CPaidModalAtom);
  const { data: userProfile } = useMyProfile();

  const { mutate: createPlan } = useCreateOnePlan({
    onSuccess: () => {
      setIsDesjardinsModalOpen(false);
      setIsActivatingDesjardins(false);
      track(Event.DesjardinsPopupAccepted);

      if (userTrialModalPreference === TrialModalType.never) {
        presentB2cPaidModal(true);
      }
    },
    onError: () => {
      setIsActivatingDesjardins(false);
    },
  });

  const onDesjardinsActivationConfirmed = () => {
    setIsActivatingDesjardins(true);
    createPlan({ code: 'DESJARDINSPC' });
  };

  const onDesjardinsActivationDismissed = () => {
    setIsDesjardinsModalOpen(false);
    setShowIntroAfterDesjardinsModal(true);
  };

  const shouldShowDesjardinsModal = isDesjardinsModalOpen && !notificationsModalVisible;

  return (
    <Modal onClose={onDesjardinsActivationDismissed} open={shouldShowDesjardinsModal} transparent>
      <Modal.Card gap="spacing.general.x6">
        <Column
          alignItems="center"
          gap="spacing.general.x4"
          paddingHorizontal="spacing.general.x4"
          paddingTop="spacing.general.x4"
        >
          <Column
            alignItems="center"
            backgroundColor="surface.success.subtle"
            borderRadius="radius.full"
            height={64}
            justifyContent="center"
            width={64}
          >
            <IconRosetteDiscountCheck color={theme.colors['surface.success.default']} size={32} strokeWidth={1.5} />
          </Column>

          <Column alignItems="center" gap="spacing.general.x2">
            <Text
              color="surface.success.default"
              fontSize={12}
              fontWeight="700"
              letterSpacing={0.6}
              textAlign="center"
              textTransform="uppercase"
            >
              {t('desjardinsModal.eyebrow')}
            </Text>
            <Text fontSize={22} fontWeight="700" lineHeight={27} textAlign="center">
              {t('desjardinsModal.title', { name: userProfile?.firstName })}
            </Text>
            <Text color="text.secondary" lineHeight={21} textAlign="center" variant="bodyBase">
              {t('desjardinsModal.description')}
            </Text>
          </Column>
        </Column>

        <Column gap="spacing.general.x3" width="100%">
          <Button
            loading={isActivatingDesjardins}
            minHeight={48}
            onPress={onDesjardinsActivationConfirmed}
            variant="primary"
          >
            {t('desjardinsModal.ctaPositive')}
          </Button>

          <Button minHeight={48} onPress={onDesjardinsActivationDismissed} variant="secondary">
            {t('desjardinsModal.ctaNegative')}
          </Button>
        </Column>
      </Modal.Card>
    </Modal>
  );
}
